<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { SEARCH_TIME_RANGE_OPTIONS, formatSelectionLabel } from './timeRangeUtils'

const props = defineProps({
  modelValue: {
    type: Object,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  // Keep the trigger in its filled state even on All Time. For toolbars where
  // it's a primary control and should read as a button rather than as a filter
  // indicator.
  alwaysEnabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const activeSection = ref('presets') // 'presets' | 'custom'
const triggerRef = ref(null)
const menuRef = ref(null)
const menuStyle = ref({})

// Custom range inputs
const customStart = ref('')
const customEnd = ref('')

// Display label for the trigger button
const displayLabel = computed(() => {
  return formatSelectionLabel(props.modelValue)
})

// Whether a filter is active (non-null selection)
const isActive = computed(() => props.modelValue !== null)

// Short label for the button
const buttonLabel = computed(() => {
  if (!props.modelValue) return 'All Time'
  if (props.modelValue.type === 'preset') {
    const opt = SEARCH_TIME_RANGE_OPTIONS.find(o => o.value === props.modelValue.value)
    return opt?.label || props.modelValue.value
  }
  if (props.modelValue.type === 'custom') return 'Custom'
  return 'All Time'
})

// Position the menu relative to the trigger button
function updateMenuPosition() {
  if (!triggerRef.value || !isOpen.value) return

  const rect = triggerRef.value.getBoundingClientRect()
  const menuWidth = 260

  let left = rect.right - menuWidth
  if (left < 10) left = 10

  menuStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 8}px`,
    left: `${left}px`,
    width: '260px'
  }
}

function toggleDropdown() {
  if (props.disabled) return
  if (!isOpen.value) {
    // Default to the section matching the current selection
    if (props.modelValue?.type === 'custom') {
      activeSection.value = 'custom'
      customStart.value = msToDateString(props.modelValue.start)
      customEnd.value = props.modelValue.end ? msToDateString(props.modelValue.end) : ''
    } else {
      activeSection.value = 'presets'
    }
  }
  isOpen.value = !isOpen.value
}

function closeDropdown() {
  isOpen.value = false
}

function selectPreset(option) {
  if (option.value === null) {
    emit('update:modelValue', null)
  } else {
    emit('update:modelValue', { type: 'preset', value: option.value })
  }
  closeDropdown()
}

function showPresets() {
  activeSection.value = 'presets'
}

function showCustomRange() {
  activeSection.value = 'custom'
  if (props.modelValue?.type === 'custom') {
    customStart.value = msToDateString(props.modelValue.start)
    customEnd.value = props.modelValue.end ? msToDateString(props.modelValue.end) : ''
  }
}

function applyCustomRange() {
  if (!customStart.value) return
  // Date inputs give YYYY-MM-DD strings; start = beginning of day, end = end of day
  const startMs = new Date(customStart.value + 'T00:00:00').getTime()
  const endMs = customEnd.value ? new Date(customEnd.value + 'T23:59:59').getTime() : null
  if (isNaN(startMs)) return
  if (endMs !== null && isNaN(endMs)) return
  emit('update:modelValue', { type: 'custom', start: startMs, end: endMs })
  closeDropdown()
}

function msToDateString(ms) {
  const d = new Date(ms)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Check if a preset option is currently selected
function isSelected(option) {
  if (option.value === null) return props.modelValue === null
  return props.modelValue?.type === 'preset' && props.modelValue?.value === option.value
}

// Close dropdown when clicking outside
function handleClickOutside(event) {
  if (triggerRef.value && triggerRef.value.contains(event.target)) return
  if (menuRef.value && menuRef.value.contains(event.target)) return
  closeDropdown()
}

function handleKeydown(event) {
  if (event.key === 'Escape') closeDropdown()
}

watch(isOpen, async (newVal) => {
  if (newVal) {
    await nextTick()
    updateMenuPosition()
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateMenuPosition)
  window.addEventListener('scroll', updateMenuPosition, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
})
</script>

<template>
  <div class="time-range-dropdown">
    <!-- Trigger Button -->
    <button
      ref="triggerRef"
      type="button"
      class="dropdown-trigger"
      :class="{ 'is-enabled': isActive || alwaysEnabled, 'is-open': isOpen }"
      :disabled="disabled"
      @click="toggleDropdown"
      :title="displayLabel"
    >
      <font-awesome-icon :icon="['fas', 'clock']" class="dropdown-trigger-icon" />
      <span class="dropdown-trigger-value">{{ buttonLabel }}</span>
      <font-awesome-icon :icon="['fas', 'chevron-down']" class="dropdown-trigger-chevron" />
    </button>

    <!-- Dropdown Menu -->
    <Teleport to="body">
      <Transition name="tr-dropdown">
        <div v-if="isOpen" ref="menuRef" class="time-range-menu" :style="menuStyle">
          <!-- Presets Section Header -->
          <button
            class="section-toggle"
            :class="{ active: activeSection === 'presets' }"
            @click="showPresets"
          >
            <font-awesome-icon :icon="['fas', 'clock']" class="section-icon" />
            <span>Relative</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="section-chevron"
              :class="{ rotated: activeSection === 'presets' }"
            />
          </button>

          <!-- Preset Options (collapsible) -->
          <div v-if="activeSection === 'presets'" class="preset-list">
            <button
              v-for="option in SEARCH_TIME_RANGE_OPTIONS"
              :key="String(option.value)"
              class="preset-option"
              :class="{ selected: isSelected(option) }"
              @click="selectPreset(option)"
            >
              <span>{{ option.label }}</span>
              <font-awesome-icon v-if="isSelected(option)" :icon="['fas', 'check']" class="check-icon" />
            </button>
          </div>

          <!-- Divider -->
          <div class="menu-divider"></div>

          <!-- Custom Range Section Header -->
          <button
            class="section-toggle"
            :class="{ active: activeSection === 'custom' }"
            @click="showCustomRange"
          >
            <font-awesome-icon :icon="['fas', 'calendar-days']" class="section-icon" />
            <span>Custom Range</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="section-chevron"
              :class="{ rotated: activeSection === 'custom' }"
            />
          </button>

          <!-- Custom Range Inputs (collapsible) -->
          <div v-if="activeSection === 'custom'" class="custom-range">
            <div class="custom-field">
              <label>Start</label>
              <input type="date" v-model="customStart" class="custom-input" />
            </div>
            <div class="custom-field">
              <label>End <span class="optional">(optional)</span></label>
              <input type="date" v-model="customEnd" class="custom-input" />
            </div>
            <button
              class="apply-btn"
              :disabled="!customStart"
              @click="applyCustomRange"
            >
              Apply
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
/* Trigger styles (.dropdown-trigger and children) come from
   @/styles/dropdown-trigger.css, imported globally in main.js. */
.time-range-dropdown {
  position: relative;
}
</style>

<!-- Unscoped styles for teleported dropdown -->
<style>
.time-range-menu {
  background: var(--surface-page);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  z-index: 9999;
  overflow: hidden;
}

/* Dropdown animation */
.tr-dropdown-enter-active,
.tr-dropdown-leave-active {
  transition: var(--transition);
}

.tr-dropdown-enter-from,
.tr-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Preset list */
.time-range-menu .preset-list {
  padding: 0.375rem;
}

.time-range-menu .preset-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: 0.8rem;
  cursor: pointer;
  transition: var(--transition-fast);
}

.time-range-menu .preset-option:hover {
  background: var(--surface-overlay-2);
  color: var(--text-heading);
}

.time-range-menu .preset-option.selected {
  color: var(--accent-text);
  background: var(--accent-soft-2);
}

.time-range-menu .check-icon {
  font-size: 0.7rem;
  color: var(--accent);
}

/* Divider */
.time-range-menu .menu-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 0.25rem 0.75rem;
}

/* Section toggle (accordion headers) */
.time-range-menu .section-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: calc(100% - 0.75rem);
  padding: 0.625rem 0.75rem;
  margin: 0.25rem 0.375rem;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;
  transition: var(--transition-fast);
}

.time-range-menu .section-toggle:hover {
  background: var(--surface-overlay-2);
  color: var(--text-primary);
}

.time-range-menu .section-toggle.active {
  color: var(--accent-text);
}

.time-range-menu .section-icon {
  font-size: 0.75rem;
  width: 1rem;
  text-align: center;
}

.time-range-menu .section-chevron {
  font-size: 0.6rem;
  margin-left: auto;
  transition: transform 0.2s ease;
}

.time-range-menu .section-chevron.rotated {
  transform: rotate(180deg);
}

/* Custom range inputs */
.time-range-menu .custom-range {
  padding: 0.75rem;
  padding-top: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.time-range-menu .custom-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.time-range-menu .custom-field label {
  font-size: 0.7rem;
  color: var(--text-faint);
  font-weight: 500;
}

.time-range-menu .custom-field .optional {
  font-weight: 400;
  color: var(--text-disabled);
}

.time-range-menu .custom-input {
  background: var(--surface-sunken);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  padding: 0.4rem 0.5rem;
  font-size: 0.75rem;
  outline: none;
  transition: border-color 0.15s ease;
  color-scheme: dark;
}

.time-range-menu .custom-input:focus {
  border-color: rgba(115, 87, 210, 0.6);
}

.time-range-menu .apply-btn {
  background: rgba(115, 87, 210, 0.2);
  border: 1px solid rgba(115, 87, 210, 0.4);
  border-radius: var(--radius-sm);
  color: var(--accent-text);
  padding: 0.4rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-fast);
  margin-top: 0.25rem;
}

.time-range-menu .apply-btn:hover:not(:disabled) {
  background: rgba(115, 87, 210, 0.35);
  border-color: var(--accent);
  color: var(--text-heading);
}

.time-range-menu .apply-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
